import Card from "@mui/material/Card";
import PropTypes from "prop-types";
import { useParams } from "react-router-dom";

// Material Kit 2 React components
import MKBox from "components/MKBox";
import MKTypography from "components/MKTypography";
import { useTranslation } from "react-i18next";

// Material Kit 2 React examples
import Navbar from "components/Navbars";
import DefaultFooter from "examples/Footers/DefaultFooter";
import FaqFromApi from "components/FaqFromApi";

// About Us page sections
import IOSHero from "./sections/ClientHeros/iOSHero";
import AndroidHero from "./sections/ClientHeros/AndroidHero";
import WindowsHero from "./sections/ClientHeros/WindowsHero";
import MacOSHero from "./sections/ClientHeros/macOSHero";
import ThirdHero from "./sections/ClientHeros/ThirdHero";
import PlatformHero from "./sections/ClientHeros/PlatformHero";
import NavTabs from "./sections/ClientHeros/NavTabs";
import IOSDesc from "./sections/desc/iOSDesc";
import AndroidDesc from "./sections/desc/AndroidDesc";
import MacOSDesc from "./sections/desc/MacOSDesc";
import WindowsDesc from "./sections/desc/WindowsDesc";
import ThirdDesc from "./sections/desc/ThirdDesc";
import LinuxDesc from "./sections/desc/LinuxDesc";
import MerlinDesc from "./sections/desc/MerlinDesc";
import OpenWrtDesc from "./sections/desc/OpenWrtDesc";
import SimpleClientDesc from "./sections/desc/SimpleClientDesc";
import { isAuthenticated } from "utils/auth";

// Routes
import routes from "routes";
import footerRoutes from "footer.routes";

function Clients({ forcedPlatform = "" }) {
  const { t, i18n } = useTranslation();

  const { platform } = useParams();
  const effectivePlatform = forcedPlatform || platform || "ios";
  const normalizedPlatform =
    effectivePlatform === "smarttv" || effectivePlatform === "tv"
      ? "android_tv"
      : effectivePlatform;

  const faqLang = i18n.language && i18n.language.toLowerCase().startsWith("zh") ? "zh" : "en";

  const renderHero = () => {
    switch (normalizedPlatform) {
      case "android":
        return <AndroidHero />;
      case "windows":
        return <WindowsHero />;
      case "macos":
        return <MacOSHero />;
      case "thirdparty":
        return <ThirdHero />;
      case "android_tv":
        return <AndroidHero platform="android_tv" clientKey="androidTv" />;
      case "apple_tv":
        return (
          <PlatformHero
            clientKey="appleTv"
            buttons={[
              { href: "#apple-tv-setup", labelKey: "clients.appleTv.setupButton" },
              { action: "sharedAppleId", labelKey: "clients.appleTv.sharedIdButton" },
            ]}
          />
        );
      case "linux":
        return (
          <PlatformHero
            clientKey="linux"
            softwarePlatform="linux"
            buttons={[{ href: "#linux-downloads", labelKey: "clients.common.viewDownloads" }]}
          />
        );
      case "openwrt":
        return (
          <PlatformHero
            clientKey="openwrt"
            softwarePlatform="openwrt"
            buttons={[{ href: "#openwrt-downloads", labelKey: "clients.common.viewDownloads" }]}
          />
        );
      case "merlin":
        return (
          <PlatformHero
            clientKey="merlin"
            softwarePlatform="merlin"
            buttons={[{ href: "#merlin-downloads", labelKey: "clients.common.viewDownloads" }]}
          />
        );
      case "ios":
      default:
        return <IOSHero />;
    }
  };

  const renderFaq = () => {
    switch (normalizedPlatform) {
      case "android":
        return (
          <FaqFromApi
            tags={["android"]}
            lang={faqLang}
            titleKey="faq.title"
            defaultExpanded={false}
            emptyHide
          />
        );
      case "windows":
        return (
          <FaqFromApi
            tags={["windows"]}
            lang={faqLang}
            titleKey="faq.title"
            defaultExpanded={false}
            emptyHide
          />
        );
      case "macos":
        return (
          <FaqFromApi
            tags={["macos"]}
            lang={faqLang}
            titleKey="faq.title"
            defaultExpanded={false}
            emptyHide
          />
        );
      case "thirdparty":
        return (
          <FaqFromApi
            tags={["third"]}
            lang={faqLang}
            titleKey="faq.title"
            defaultExpanded={false}
            emptyHide
          />
        );
      case "android_tv":
      case "apple_tv":
        return (
          <FaqFromApi
            tags={["tv", "android_tv", "apple_tv"]}
            lang={faqLang}
            titleKey="faq.title"
            defaultExpanded={false}
            emptyHide
          />
        );
      case "linux":
        return (
          <FaqFromApi
            tags={["linux"]}
            lang={faqLang}
            titleKey="faq.title"
            defaultExpanded={false}
            emptyHide
          />
        );
      case "openwrt":
      case "merlin":
        return (
          <FaqFromApi
            tags={["router", "openwrt", "merlin"]}
            lang={faqLang}
            titleKey="faq.title"
            defaultExpanded={false}
            emptyHide
          />
        );
      case "ios":
      default:
        return (
          <FaqFromApi
            tags={["ios"]}
            lang={faqLang}
            titleKey="faq.title"
            defaultExpanded={false}
            emptyHide
          />
        );
    }
  };

  const isLoggedIn = isAuthenticated();
  const showIOSDesc = normalizedPlatform === "ios";
  const showAndroidDesc = normalizedPlatform === "android";
  const showWindowsDesc = normalizedPlatform === "windows";
  const showMacOSDesc = normalizedPlatform === "macos";
  const showThirdDesc = normalizedPlatform === "thirdparty";
  const showAndroidTvDesc = normalizedPlatform === "android_tv";
  const showAppleTvDesc = normalizedPlatform === "apple_tv";
  const showLinuxDesc = normalizedPlatform === "linux";
  const showOpenWrtDesc = normalizedPlatform === "openwrt";
  const showMerlinDesc = normalizedPlatform === "merlin";
  const hasDescSection =
    showIOSDesc ||
    showAndroidDesc ||
    showWindowsDesc ||
    showMacOSDesc ||
    showThirdDesc ||
    showAndroidTvDesc ||
    showAppleTvDesc ||
    showLinuxDesc ||
    showOpenWrtDesc ||
    showMerlinDesc;
  const faqCardMt = hasDescSection ? 0 : -8;

  return (
    <>
      <Navbar
        routes={routes}
        action={
          isLoggedIn
            ? {
                type: "internal",
                route: "/userinfo/",
                label: t("nav.userCenter"),
                color: "info",
              }
            : {
                type: "internal",
                route: "/signup/",
                label: t("nav.signup"),
                color: "info",
              }
        }
        transparent
        light
      />
      {renderHero()}
      <MKBox mt={-6} mb={2} position="relative" zIndex={2}>
        <NavTabs />
      </MKBox>
      {showIOSDesc ? <IOSDesc /> : null}
      {showAndroidDesc ? <AndroidDesc /> : null}
      {showWindowsDesc ? <WindowsDesc /> : null}
      {showMacOSDesc ? <MacOSDesc /> : null}
      {showThirdDesc ? <ThirdDesc /> : null}
      {showAndroidTvDesc ? <SimpleClientDesc clientKey="androidTv" /> : null}
      {showAppleTvDesc ? (
        <MKBox id="apple-tv-setup">
          <SimpleClientDesc clientKey="appleTv" />
        </MKBox>
      ) : null}
      {showLinuxDesc ? (
        <MKBox id="linux-downloads">
          <LinuxDesc />
        </MKBox>
      ) : null}
      {showOpenWrtDesc ? (
        <MKBox id="openwrt-downloads">
          <OpenWrtDesc />
        </MKBox>
      ) : null}
      {showMerlinDesc ? (
        <MKBox id="merlin-downloads">
          <MerlinDesc />
        </MKBox>
      ) : null}
      <Card
        sx={{
          p: { xs: 2, md: 3 },
          mx: { xs: 2, lg: 3 },
          mt: faqCardMt,
          mb: 4,
          boxShadow: ({ boxShadows: { xxl } }) => xxl,
        }}
      >
        <MKTypography variant="h4" fontWeight="bold" mb={2}>
          {t("faq.title")}
        </MKTypography>
        {renderFaq()}
      </Card>
      <MKBox pt={6} px={1} mt={6}>
        <DefaultFooter content={footerRoutes} />
      </MKBox>
    </>
  );
}

Clients.propTypes = {
  forcedPlatform: PropTypes.string,
};

export default Clients;
